import { InvoiceStatus, InvoiceType } from 'models/EInvoice';

export const INVOICE_STATUS_COLOR_MAP: Record<string, string> = {
  [InvoiceStatus.ISSUED]: 'success',
  [InvoiceStatus.PENDING]: 'warning',
  [InvoiceStatus.DRAFT]: 'processing',
  [InvoiceStatus.CANCELED]: 'error',
  [InvoiceStatus.FAILED]: 'error',
};

export const INVOICE_TYPE_COLOR_MAP: Partial<Record<number, string>> = {
  [InvoiceType.NEW]: 'blue',
  [InvoiceType.CANCELLED]: 'volcano',
  [InvoiceType.REPLACEMENT]: 'gold',
  [InvoiceType.REPLACED]: 'default',
  [InvoiceType.ADJUST_INCREASE]: 'cyan',
  [InvoiceType.ADJUST_DECREASE]: 'orange',
  [InvoiceType.ADJUST_INFO]: 'purple',
  [InvoiceType.ADJUST_BOTH]: 'magenta',
  [InvoiceType.ADJUSTED]: 'geekblue',
};

export const INVOICE_STATUS_TEXT_CLASS_MAP: Record<string, string> = {
  [InvoiceStatus.ISSUED]: 'text-[var(--sematicSuccessColor500)]',
  [InvoiceStatus.PENDING]: 'text-[var(--sematicErrorColor600)]',
  [InvoiceStatus.DRAFT]: 'text-gray-500',
  [InvoiceStatus.CANCELED]: 'text-[var(--sematicErrorColor500)]',
  [InvoiceStatus.FAILED]: 'text-[var(--sematicErrorColor500)]',
};

export const getInvoiceTagColor = (status: InvoiceStatus | null, invoiceType?: number): string => {
  if (invoiceType !== undefined && INVOICE_TYPE_COLOR_MAP[invoiceType]) {
    return INVOICE_TYPE_COLOR_MAP[invoiceType]!;
  }
  if (status && INVOICE_STATUS_COLOR_MAP[status]) {
    return INVOICE_STATUS_COLOR_MAP[status];
  }
  return 'default';
};

export const getInvoiceStatusTextClass = (status: InvoiceStatus | null): string => {
  if (status && INVOICE_STATUS_TEXT_CLASS_MAP[status]) {
    return INVOICE_STATUS_TEXT_CLASS_MAP[status]!;
  }

  return 'text-gray-500';
};

export const isInvoiceError = (status: InvoiceStatus | null, invoiceType?: number): boolean => {
  return status === InvoiceStatus.FAILED || invoiceType === InvoiceType.REPLACED;
};

export const canEditDraftInvoice = ({ status }: { status: InvoiceStatus | null }): boolean =>
  status === InvoiceStatus.DRAFT;

export const canSignInvoice = ({
  status,
  providerInvoiceId,
  invoiceType,
}: {
  status: InvoiceStatus | null;
  providerInvoiceId?: string;
  invoiceType?: number;
}): boolean => {
  if (status !== InvoiceStatus.DRAFT) return false;
  if (!providerInvoiceId) return false;

  return ![InvoiceType.REPLACED, InvoiceType.CANCELLED, InvoiceType.ADJUSTED].includes(invoiceType || -1);
};

export const canReplaceInvoice = ({
  status,
  invoiceType,
}: {
  status: InvoiceStatus | null;
  invoiceType?: number;
}): boolean => {
  if (status !== InvoiceStatus.ISSUED) return false;

  // Rule nghiệp vụ:
  // - Chỉ thay thế hóa đơn gốc đang hiệu lực.
  // - Khi hóa đơn đã từng phát sinh điều chỉnh thì không được thay thế nữa.
  // - Hóa đơn con điều chỉnh / hóa đơn đã bị thay thế / hóa đơn hủy không được thao tác.
  return ![
    InvoiceType.DRAFT,
    InvoiceType.CANCELLED,
    InvoiceType.REPLACED,
    InvoiceType.ADJUST_INCREASE,
    InvoiceType.ADJUST_DECREASE,
    InvoiceType.ADJUST_INFO,
    InvoiceType.ADJUST_BOTH,
    InvoiceType.ADJUSTED,
  ].includes(invoiceType || -1);
};

export const canAdjustInvoice = ({
  status,
  invoiceType,
}: {
  status: InvoiceStatus | null;
  invoiceType?: number;
}): boolean => {
  if (status !== InvoiceStatus.ISSUED) return false;

  // Rule nghiệp vụ:
  // - Hóa đơn gốc có thể điều chỉnh nhiều lần.
  // - Hóa đơn con điều chỉnh không được điều chỉnh/thay thế tiếp.
  // - Hóa đơn đã bị thay thế / hủy không được thao tác.
  return ![
    InvoiceType.DRAFT,
    InvoiceType.CANCELLED,
    InvoiceType.REPLACED,
    InvoiceType.ADJUST_INCREASE,
    InvoiceType.ADJUST_DECREASE,
    InvoiceType.ADJUST_INFO,
    InvoiceType.ADJUST_BOTH,
  ].includes(invoiceType || -1);
};
